/**
 * Itinerary API — thin wrapper over `@wg/api/itinerary`'s embed-flavored
 * surface (`createEmbedItineraryApi` + the embed wire codecs). Endpoint
 * paths, status mapping and display_data round-tripping all live in the
 * shared module; this file only injects the embed's instance-scoped authed
 * client and adapts the shared types onto the context's local aliases.
 *
 * Auth comes from the embed's authedClient (httpClient) which attaches the
 * visitor's session JWT (guest or authenticated). The backend's
 * `get_current_user` dependency accepts both — guest users can own and
 * mutate itineraries, scoped by their guest user_id.
 */
import {
  createEmbedItineraryApi,
  embedItemFromWire,
  embedItemToCreatePayload,
  embedItemToUpdatePayload,
  type EmbedItineraryWire,
  type EmbedItineraryItemWire,
  type EmbedItineraryUpdatePatch,
  type EmbedInvite,
  type EmbedInvitePreview,
  type EmbedJoinResult,
  type EmbedMember,
  type EmbedItineraryRole,
  type EmbedTripSummary,
} from '@wg/api/itinerary';
import { httpClient } from '../client';
import type { ItineraryItem } from '../context/ItineraryContext';

// Local aliases kept for existing import sites.
export type ItineraryWire = EmbedItineraryWire;
export type ItineraryItemWire = EmbedItineraryItemWire;
export type ItineraryUpdatePatch = EmbedItineraryUpdatePatch;

const api = createEmbedItineraryApi(httpClient);

/** Decode an embed item from a backend wire row. */
export function fromWire(row: ItineraryItemWire): ItineraryItem {
  // The shared EmbedItineraryItem is structurally identical to the
  // context's ItineraryItem; the cast just re-labels the type.
  return embedItemFromWire(row) as ItineraryItem;
}

/** Build the create-item payload for the backend from an embed item. */
export function toCreatePayload(
  item: Partial<ItineraryItem> & { title: string },
): Record<string, unknown> {
  return embedItemToCreatePayload(item);
}

/** Build the update-item payload (PUT). Only includes provided fields. */
export function toUpdatePayload(
  current: ItineraryItem,
  patch: Partial<ItineraryItem>,
): Record<string, unknown> {
  return embedItemToUpdatePayload(current, patch);
}

export const listItineraries = api.listItineraries;
export const createItinerary = api.createItinerary;
export const getItineraryWithItems = api.getItineraryWithItems;
export const createItineraryItem = api.createItineraryItem;
export const updateItineraryItem = api.updateItineraryItem;
export const deleteItineraryItem = api.deleteItineraryItem;
export const updateItinerary = api.updateItinerary;
export const shareItinerary = api.shareItinerary;
export const revokeShare = api.revokeShare;
export const createInvite = api.createInvite;
export const revokeInvite = api.revokeInvite;
export const previewInvite = api.previewInvite;
export const joinItinerary = api.joinItinerary;
export const listMembers = api.listMembers;
export const removeMember = api.removeMember;
export const listMemberships = api.listMemberships;

export type {
  EmbedInvite,
  EmbedInvitePreview,
  EmbedJoinResult,
  EmbedMember,
  EmbedItineraryRole,
  EmbedTripSummary,
};
